import { CommandItem, Redact, Tooltip } from '../../../../core'
import type { CommandItemProps } from '../../../../core'
import { useApiOrgSlug } from '../../../utils/orgSlug'
import { EncounterEnrichmentStatusBadge } from './OverridableEncounterEnrichmentStatusBadge'
import type { components } from '@joyful/openapi/types.gen'
import { formatDate, cn } from '@joyful/utils'
import { TriangleAlertIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'

export interface EncounterCommandItemProps extends Partial<
  Omit<CommandItemProps, 'children'>
> {
  account?: Pick<components['schemas']['AccountRead'], 'name' | 'slug'>
  encounter: Pick<
    components['schemas']['EncounterAPIResponse'],
    | 'enrichment_status'
    | 'enrichment_status_override'
    | 'enrichment_status_override_reason'
    | 'patient_name'
    | 'rendering_provider_name'
    | 'start_date'
    | 'id'
  >
  titleClassName?: string
}

export function EncounterCommandItem({
  account,
  encounter,
  titleClassName,
  ...commandItemProps
}: EncounterCommandItemProps) {
  return (
    <CommandItem
      size="xl"
      value={encounter.id}
      keywords={[
        encounter.patient_name,
        encounter.rendering_provider_name,
        encounter.start_date,
      ].filter(Boolean)}
      endAdornment={
        encounter.enrichment_status ? (
          <EncounterEnrichmentStatusBadge
            paidBy={null}
            size="sm"
            status={encounter.enrichment_status}
            statusOverride={encounter.enrichment_status_override}
            statusOverrideReason={encounter.enrichment_status_override_reason}
          />
        ) : undefined
      }
      subtitle={
        <EncounterItemSubtitle account={account} encounter={encounter} />
      }
      {...commandItemProps}
    >
      <Redact>
        <span className={titleClassName}>{encounter.patient_name}</span>
      </Redact>
    </CommandItem>
  )
}

export interface EncounterItemSubtitleProps
  extends
    Omit<ComponentProps<'p'>, 'children'>,
    Pick<EncounterCommandItemProps, 'account'> {
  encounter: Pick<
    components['schemas']['EncounterAPIResponse'],
    'rendering_provider_name' | 'start_date'
  >
  endAdornment?: ReactNode
}

export function EncounterItemSubtitle({
  account,
  className,
  encounter,
  endAdornment,
  ...props
}: EncounterItemSubtitleProps) {
  const orgSlug = useApiOrgSlug()

  const formattedServiceDate = formatDate(encounter.start_date, {
    shouldSkipCurrentYear: true,
  })

  return (
    <p className={cn('text-tremor-content text-xs', className)} {...props}>
      <strong>{`DOS: `}</strong>
      {formattedServiceDate}
      <strong className="ml-2">{` Provider: `}</strong>
      <Redact>{encounter.rendering_provider_name}</Redact>
      {endAdornment}

      {account !== undefined && orgSlug !== account.slug ? (
        <Tooltip
          content={
            <p>
              {`This is a `}
              <Redact asChild>
                <strong>{account.name}</strong>
              </Redact>
              {` encounter.`}
            </p>
          }
        >
          <TriangleAlertIcon className="text-tremor-brand ml-1.5 inline size-4 stroke-2" />
        </Tooltip>
      ) : null}
    </p>
  )
}
